import { defineStore } from 'pinia'
import { mergeRebetDraws, rebetLineKey } from '@/utils/rebetLines'

export interface BetslipItem {
  market_id: string | number
  market_group_id: string | number
  game_id: string | number
  game_name?: string
  draws: Array<{draw_date: string, cutoff: string, event_id: number}>
  allAvailableDraws?: Array<{draw_date: string, cutoff: string, event_id: number}> 
  drawCount?: number
  numbers: number[][]
  stake: number
  betType?: string
  maxStake?: number
  odds?: number
  numBets?: number
  picks?: number
  outcome_id?: string
  game_market_id?: number
  label?: string
  market_name?: string
  market_description?: string
  specifier?: string | null
  select_numbers?: boolean
  currency_symbol?: string
}

// Helper functions for localStorage persistence
const BETSLIP_STORAGE_KEY = 'betslip_data'
const BETSLIP_CLEARED_KEY = 'betslip_cleared_timestamp'
const LAST_STAKE_KEY = 'last_stake_value'

const saveToStorage = (betslip: BetslipItem[]) => {
  try {
    localStorage.setItem(BETSLIP_STORAGE_KEY, JSON.stringify(betslip))
    // Remove the cleared timestamp when saving new data
    if (betslip.length > 0) {
      localStorage.removeItem(BETSLIP_CLEARED_KEY)
    }
  } catch (error) {
    console.warn('Failed to save betslip to localStorage:', error)
  }
}

const loadFromStorage = (): BetslipItem[] => {
  try {
    // Check if betslip was recently cleared
    const clearedTimestamp = localStorage.getItem(BETSLIP_CLEARED_KEY)
    if (clearedTimestamp) {
      const timeSinceCleared = Date.now() - parseInt(clearedTimestamp)
      if (timeSinceCleared < 10000) { // 10 seconds for safety
        return []
      }
    }
    
    const stored = localStorage.getItem(BETSLIP_STORAGE_KEY)
    const result = stored ? JSON.parse(stored) : []
    return result
  } catch (error) {
    console.warn('Failed to load betslip from localStorage:', error)
    return []
  }
}

const saveLastStake = (stake: number) => {
  try {
    localStorage.setItem(LAST_STAKE_KEY, stake.toString())
  } catch (error) {
    console.warn('Failed to save last stake to localStorage:', error)
  }
}

const loadLastStake = (): number | null => {
  try {
    const stored = localStorage.getItem(LAST_STAKE_KEY)
    const result = stored ? parseFloat(stored) : null
    return result
  } catch (error) {
    console.warn('Failed to load last stake from localStorage:', error)
    return null
  }
}

export const useBetslipStore = defineStore('betslip', {
  state: () => ({
    betslip: loadFromStorage() as BetslipItem[],
    lastStake: loadLastStake() as number | null
  }),

  getters: {
    getBetslip: (state) => state.betslip,
    getBetslipCount: (state) => state.betslip.length,
    hasBets: (state) => state.betslip.length > 0,
    getLastStake: (state) => state.lastStake
  },

  actions: {
    addRebet(bet: BetslipItem) {
      const key = rebetLineKey(bet)
      const existingIndex = this.betslip.findIndex((item) => rebetLineKey(item) === key)
      if (existingIndex === -1) {
        // Rebet retains the wager's stake rather than the last manually entered stake.
        this.betslip.push(bet)
        saveToStorage(this.betslip)
        return { changed: true, item: bet }
      }

      const existing = this.betslip[existingIndex]
      const draws = mergeRebetDraws(existing.draws, bet.draws)
      const item = { ...existing, draws, drawCount: draws.length, allAvailableDraws: bet.allAvailableDraws }
      const changed = JSON.stringify(existing) !== JSON.stringify(item)
      if (changed) {
        this.betslip[existingIndex] = item
        saveToStorage(this.betslip)
      }
      return { changed, item }
    },

    addBet(bet: BetslipItem) {
      // Check if this bet already exists in the betslip
      // IMPORTANT: Include game_id in duplicate check to allow same bet in different games
      const existingBetIndex = this.betslip.findIndex(existingBet => {
        // First check if it's the same game
        if (existingBet.game_id !== bet.game_id) {
          return false; // Different games, not a duplicate
        }
        
        // For bets that don't require number selection (select_numbers !== 'yes')
        // Check if market_id, market_group_id, and outcome_id match
        if (bet.numbers.length === 0) {
          if (bet.game_market_id && existingBet.game_market_id) {
            return existingBet.game_id === bet.game_id &&
              existingBet.game_market_id === bet.game_market_id
          }

          const existingSpecifier = existingBet.specifier != null ? String(existingBet.specifier) : ''
          const betSpecifier = bet.specifier != null ? String(bet.specifier) : ''

          return existingBet.market_id === bet.market_id &&
                 existingBet.market_group_id === bet.market_group_id &&
                 existingBet.outcome_id === bet.outcome_id &&
                 existingSpecifier === betSpecifier
        }
        
        // For bets that require number selection (select_numbers === true)
        // Check if market_id, market_group_id, outcome_id, and numbers match
        return existingBet.market_id === bet.market_id &&
               existingBet.market_group_id === bet.market_group_id &&
               existingBet.outcome_id === bet.outcome_id &&
               JSON.stringify(existingBet.numbers) === JSON.stringify(bet.numbers)
      })
      
      if (existingBetIndex !== -1) {
        // Bet already exists, don't add it again
        return false
      }
      
      // Use last stake if available, otherwise keep the original stake
      if (this.lastStake !== null) {
        bet.stake = this.lastStake
      }
      
      this.betslip.push(bet)
      saveToStorage(this.betslip)
      return true
    },

    removeBet(index: number) {
      this.betslip.splice(index, 1)
      saveToStorage(this.betslip)
    },

    clearBetslip() {
      this.betslip = []
      // Set timestamp BEFORE clearing to prevent reloading old data
      try {
        localStorage.setItem(BETSLIP_CLEARED_KEY, Date.now().toString())
        // Completely remove betslip data from localStorage instead of saving empty array
        localStorage.removeItem(BETSLIP_STORAGE_KEY)
      } catch (error) {
        console.warn('Failed to clear betslip from localStorage:', error)
      }
    },

    updateBet(index: number, bet: BetslipItem) {
      if (index >= 0 && index < this.betslip.length) {
        this.betslip[index] = bet
        saveToStorage(this.betslip)
      }
    },
    saveToStorage() {
      saveToStorage(this.betslip)
    },

    updateLastStake(stake: number) {
      this.lastStake = stake
      saveLastStake(stake)
    }
  }
})
